import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import toast from 'react-hot-toast';
import {
  ChevronLeftIcon,
  ExclamationCircleIcon,
  PaperAirplaneIcon,
  PhotoIcon,
  DevicePhoneMobileIcon,
  ComputerDesktopIcon,
  GlobeAltIcon
} from '@heroicons/react/24/outline';

const BugReport = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  
  const [formData, setFormData] = useState({
    category: '',
    title: '',
    description: '',
    steps_to_reproduce: '',
    expected_behavior: '',
    actual_behavior: '',
    browser: '',
    device: '',
    os: '',
    priority: 'medium',
    email: user?.email || ''
  });
  
  const [attachments, setAttachments] = useState([]);
  const [loading, setLoading] = useState(false);

  const categories = [
    { value: 'ui_bug', label: 'UI・表示の問題', icon: ComputerDesktopIcon },
    { value: 'functional_bug', label: '機能の不具合', icon: ExclamationCircleIcon },
    { value: 'performance', label: 'パフォーマンスの問題', icon: DevicePhoneMobileIcon },
    { value: 'login_auth', label: 'ログイン・認証の問題', icon: GlobeAltIcon },
    { value: 'payment', label: '支払い・報酬の問題', icon: ExclamationCircleIcon },
    { value: 'notification', label: '通知の問題', icon: DevicePhoneMobileIcon },
    { value: 'search', label: '検索機能の問題', icon: GlobeAltIcon },
    { value: 'other', label: 'その他', icon: ExclamationCircleIcon }
  ];

  const priorities = [
    { value: 'low', label: '低（軽微な問題）', color: 'green' },
    { value: 'medium', label: '中（通常の問題）', color: 'yellow' },
    { value: 'high', label: '高（重要な問題）', color: 'orange' },
    { value: 'critical', label: '緊急（システム停止）', color: 'red' }
  ];

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleFileChange = (e) => {
    const files = Array.from(e.target.files);
    const maxSize = 5 * 1024 * 1024; // 5MB
    const allowedTypes = ['image/jpeg', 'image/png', 'image/gif', 'video/mp4', 'video/webm'];
    
    const validFiles = files.filter(file => {
      if (file.size > maxSize) {
        toast.error(`${file.name}のファイルサイズが大きすぎます（最大5MB）`);
        return false;
      }
      if (!allowedTypes.includes(file.type)) {
        toast.error(`${file.name}のファイル形式がサポートされていません`);
        return false;
      }
      return true;
    });

    setAttachments([...attachments, ...validFiles]);
  };

  const removeAttachment = (index) => {
    setAttachments(attachments.filter((_, i) => i !== index));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.category || !formData.title || !formData.description) {
      toast.error('必須項目を入力してください');
      return;
    }

    setLoading(true);
    try {
      // バグレポート送信のAPI呼び出し（実装待ち）
      // const formDataWithFiles = new FormData();
      // Object.keys(formData).forEach(key => {
      //   formDataWithFiles.append(key, formData[key]);
      // });
      // attachments.forEach((file, index) => {
      //   formDataWithFiles.append(`attachment_${index}`, file);
      // });

      await new Promise(resolve => setTimeout(resolve, 1000)); // ダミー処理
      
      toast.success('バグレポートを送信しました。ご報告ありがとうございます。');
      
      // フォームをリセット
      setFormData({
        category: '',
        title: '',
        description: '',
        steps_to_reproduce: '',
        expected_behavior: '',
        actual_behavior: '',
        browser: '',
        device: '',
        os: '',
        priority: 'medium',
        email: user?.email || ''
      });
      setAttachments([]);
      
      // 送信完了後、マイページに戻る
      setTimeout(() => {
        navigate('/mypage');
      }, 1500);
      
    } catch (error) {
      toast.error('送信に失敗しました。しばらく時間をおいて再度お試しください。');
    } finally {
      setLoading(false);
    }
  };

  // ブラウザ情報を自動取得
  React.useEffect(() => {
    const userAgent = navigator.userAgent;
    let browser = 'Unknown';
    
    if (userAgent.includes('Chrome')) browser = 'Chrome';
    else if (userAgent.includes('Firefox')) browser = 'Firefox';
    else if (userAgent.includes('Safari')) browser = 'Safari';
    else if (userAgent.includes('Edge')) browser = 'Edge';
    
    const platform = navigator.platform;
    
    setFormData(prev => ({
      ...prev,
      browser: browser,
      device: /Mobi|Android/i.test(userAgent) ? 'Mobile' : 'Desktop',
      os: platform
    }));
  }, []);

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center h-16">
            <button
              onClick={() => navigate('/mypage')}
              className="mr-4 p-2 text-gray-600 hover:text-gray-900"
            >
              <ChevronLeftIcon className="w-6 h-6" />
            </button>
            <h1 className="text-xl font-bold text-gray-900">不具合・改善要望の報告</h1>
          </div>
        </div>
      </div>

      <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* 案内文 */}
        <div className="bg-blue-50 rounded-lg p-6 mb-8">
          <div className="flex items-start">
            <ExclamationCircleIcon className="w-6 h-6 text-blue-600 mr-3 flex-shrink-0 mt-0.5" />
            <div>
              <h2 className="text-lg font-semibold text-blue-900 mb-2">不具合報告にご協力ください</h2>
              <p className="text-blue-800 text-sm leading-relaxed">
                バイターズをより良いサービスにするため、不具合や改善のご提案をお聞かせください。
                詳細な情報をいただくことで、より迅速に問題を解決できます。
              </p>
            </div>
          </div>
        </div>

        {/* バグレポートフォーム */}
        <div className="bg-white rounded-lg shadow-sm p-6">
          <form onSubmit={handleSubmit} className="space-y-6">
            {/* カテゴリ選択 */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-3">
                問題のカテゴリ <span className="text-red-500">*</span>
              </label>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {categories.map((category) => {
                  const IconComponent = category.icon;
                  return (
                    <label
                      key={category.value}
                      className={`relative flex items-center p-4 cursor-pointer rounded-lg border-2 transition-colors ${
                        formData.category === category.value
                          ? 'border-blue-500 bg-blue-50'
                          : 'border-gray-200 hover:border-gray-300'
                      }`}
                    >
                      <input
                        type="radio"
                        name="category"
                        value={category.value}
                        checked={formData.category === category.value}
                        onChange={handleChange}
                        className="sr-only"
                      />
                      <IconComponent className="w-5 h-5 mr-3 text-gray-400" />
                      <span className={`font-medium ${
                        formData.category === category.value ? 'text-blue-700' : 'text-gray-900'
                      }`}>
                        {category.label}
                      </span>
                    </label>
                  );
                })}
              </div>
            </div>

            {/* 優先度 */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-3">
                優先度 <span className="text-red-500">*</span>
              </label>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                {priorities.map((priority) => (
                  <label
                    key={priority.value}
                    className={`relative flex items-center justify-center p-3 cursor-pointer rounded-lg border-2 transition-colors ${
                      formData.priority === priority.value
                        ? `border-${priority.color}-500 bg-${priority.color}-50`
                        : 'border-gray-200 hover:border-gray-300'
                    }`}
                  >
                    <input
                      type="radio"
                      name="priority"
                      value={priority.value}
                      checked={formData.priority === priority.value}
                      onChange={handleChange}
                      className="sr-only"
                    />
                    <span className={`text-sm font-medium text-center ${
                      formData.priority === priority.value ? `text-${priority.color}-700` : 'text-gray-900'
                    }`}>
                      {priority.label}
                    </span>
                  </label>
                ))}
              </div>
            </div>

            {/* タイトル */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                問題のタイトル <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                name="title"
                value={formData.title}
                onChange={handleChange}
                placeholder="例: ログインボタンを押しても反応しない"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                required
              />
            </div>

            {/* 問題の詳細 */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                問題の詳細 <span className="text-red-500">*</span>
              </label>
              <textarea
                name="description"
                value={formData.description}
                onChange={handleChange}
                rows={4}
                placeholder="発生している問題について詳しく説明してください"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 resize-vertical"
                required
              />
            </div>

            {/* 再現手順 */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                再現手順
              </label>
              <textarea
                name="steps_to_reproduce"
                value={formData.steps_to_reproduce}
                onChange={handleChange}
                rows={3}
                placeholder="問題を再現するための手順を順番に書いてください&#10;1. ログインページを開く&#10;2. メールアドレスとパスワードを入力&#10;3. ログインボタンをクリック"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 resize-vertical"
              />
            </div>

            {/* 期待される動作と実際の動作 */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  期待される動作
                </label>
                <textarea
                  name="expected_behavior"
                  value={formData.expected_behavior}
                  onChange={handleChange}
                  rows={3}
                  placeholder="どのような動作を期待していましたか？"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 resize-vertical"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  実際の動作
                </label>
                <textarea
                  name="actual_behavior"
                  value={formData.actual_behavior}
                  onChange={handleChange}
                  rows={3}
                  placeholder="実際にはどのような動作になりましたか？"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 resize-vertical"
                />
              </div>
            </div>

            {/* 環境情報 */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  ブラウザ
                </label>
                <input
                  type="text"
                  name="browser"
                  value={formData.browser}
                  onChange={handleChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="Chrome, Firefox, Safari等"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  デバイス
                </label>
                <input
                  type="text"
                  name="device"
                  value={formData.device}
                  onChange={handleChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="PC, スマートフォン等"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  OS
                </label>
                <input
                  type="text"
                  name="os"
                  value={formData.os}
                  onChange={handleChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="Windows, macOS, iOS等"
                />
              </div>
            </div>

            {/* 添付ファイル */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                スクリーンショット・動画
              </label>
              <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center">
                <PhotoIcon className="w-8 h-8 text-gray-400 mx-auto mb-2" />
                <p className="text-sm text-gray-600 mb-2">
                  問題の画面をキャプチャして添付してください（任意）
                </p>
                <input
                  type="file"
                  multiple
                  accept="image/*,video/*"
                  onChange={handleFileChange}
                  className="hidden"
                  id="file-upload"
                />
                <label
                  htmlFor="file-upload"
                  className="cursor-pointer bg-white text-blue-600 px-4 py-2 rounded-lg border border-blue-600 hover:bg-blue-50 transition-colors"
                >
                  ファイルを選択
                </label>
                <p className="text-xs text-gray-500 mt-1">
                  JPEG, PNG, GIF, MP4, WebM（最大5MB）
                </p>
              </div>
              
              {/* 添付ファイル一覧 */}
              {attachments.length > 0 && (
                <div className="mt-3">
                  <p className="text-sm font-medium text-gray-700 mb-2">添付ファイル:</p>
                  <div className="space-y-2">
                    {attachments.map((file, index) => (
                      <div key={index} className="flex items-center justify-between bg-gray-50 p-2 rounded">
                        <span className="text-sm text-gray-700 truncate">{file.name}</span>
                        <button
                          type="button"
                          onClick={() => removeAttachment(index)}
                          className="text-red-500 hover:text-red-700 text-sm"
                        >
                          削除
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* メールアドレス */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                メールアドレス <span className="text-red-500">*</span>
              </label>
              <input
                type="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                required
              />
              <p className="text-xs text-gray-500 mt-1">
                回答をお送りするメールアドレスです
              </p>
            </div>

            {/* 送信ボタン */}
            <button
              type="submit"
              disabled={loading || !formData.category || !formData.title || !formData.description}
              className="w-full bg-blue-600 text-white py-3 px-6 rounded-lg font-medium hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center justify-center"
            >
              {loading ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                  送信中...
                </>
              ) : (
                <>
                  <PaperAirplaneIcon className="w-5 h-5 mr-2" />
                  バグレポートを送信
                </>
              )}
            </button>
          </form>
        </div>

        {/* 注意事項 */}
        <div className="mt-8 bg-yellow-50 rounded-lg p-6">
          <h4 className="font-medium text-yellow-900 mb-3">バグレポートについて</h4>
          <ul className="text-sm text-yellow-800 space-y-2">
            <li> 詳細な情報をご提供いただくことで、より迅速に問題を解決できます</li>
            <li> スクリーンショットや動画があると問題の特定に非常に有効です</li>
            <li> 緊急度の高い問題は優先的に対応いたします</li>
            <li> 通常、1-3営業日以内に初回回答をお送りします</li>
            <li> 同様の問題が既に報告されている場合があります</li>
          </ul>
        </div>
      </div>
    </div>
  );
};

export default BugReport;